/* eslint-disable ember/no-computed-properties-in-native-classes, ember/no-side-effects */
/* eslint-disable ember/no-runloop */
import { computed } from '@ember/object';
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import getChartColor from 'frontend-utils/utils/hotel-chart-color';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import HotelStrategyTooltipsEvolutionChartTooltip from 'lumen/components/hotel/strategy/tooltips/evolution-chart-tooltip';
import { addNoDataZone, addZone, NO_DATA_ZONE } from 'lumen/utils/charts/zones';
import { HIGHCHARTS_MISSING_MARKER } from 'lumen/utils/constants/general-constants';
import {
  SUBTYPE_VALUES_KEY_STRATEGY,
  TYPE_ID_MEALTYPE,
} from 'lumen/utils/constants/rate-strategy-constants';
import {
  formatCancellationpolicyValue,
  formatStrategyValue,
} from 'lumen/utils/strategy/format-helper-utils';

interface HotelStrategyChartsChartEvolutionSignature {
  Args: {
    containerHeight: unknown;
    params: unknown;
  };
}

export default class HotelStrategyChartsChartEvolution extends Component<HotelStrategyChartsChartEvolutionSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  // eslint-disable-next-line ember/no-unused-services
  @service declare intl: Services['intl'];
  @service declare rsSelections: Services['rs-selections'];

  @tracked hoveredHotels = [];
  @tracked tooltipData = [];
  @tracked week = '';

  // Doesn't need to be tracked as chartData shouldn't update when this changes
  hiddenSeries = new Set();
  isAbsolute = false;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    const seriesMap = {};
    // First add the own hotel series
    const ownHotelSeries = {
      // @ts-expect-error: Incorrect type
      name: this.ownHotelInfo.name,
      // @ts-expect-error: Incorrect type
      hotelinfo: this.ownHotelInfo.id,
      data: [],
      zones: [],
      type: 'line',
      zoneAxis: 'x',
      zIndex: 2,
      lineWidth: 5,
      // @ts-expect-error: Incorrect type
      visible: !this.hiddenSeries.has(this.ownHotelInfo.id),
      color: getChartColor(
        // @ts-expect-error: Incorrect type
        this.ownHotelInfo.id,
        this.hotelAndMarketService.primaryAndSecondaryCompetitors,
      ),
      marker: {
        enabled: true,
        radius: 5,
      },
      events: {
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        legendItemClick() {
          // @ts-expect-error: Incorrect type
          if (this.visible) {
            // @ts-expect-error: Incorrect type
            that.hiddenSeries.add(that.ownHotelInfo.id);
          } else {
            // @ts-expect-error: Incorrect type
            that.hiddenSeries.delete(that.ownHotelInfo.id);
          }
        },
      },
    };

    const series = [ownHotelSeries];

    // @ts-expect-error: Incorrect type
    seriesMap[this.ownHotelInfo.id] = ownHotelSeries;

    // Then all of the competitors (activecompset will be the primary compset)
    this.rsSelections.filteredCompetitors.forEach((competitor) => {
      const competitorSeries = {
        name: competitor.name,
        hotelinfo: competitor.id,
        data: [],
        zones: [],
        zoneAxis: 'x',
        type: 'line',
        lineWidth: 2,
        visible: !this.hiddenSeries.has(competitor.id),
        color: getChartColor(
          // @ts-expect-error: Incorrect type
          competitor.id,
          this.hotelAndMarketService.primaryAndSecondaryCompetitors,
        ),
        zIndex: 1,
        marker: {
          enabled: true,
          radius: 4,
        },
        events: {
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          legendItemClick() {
            // @ts-expect-error: Incorrect type
            if (this.visible) {
              that.hiddenSeries.add(competitor.id);
            } else {
              that.hiddenSeries.delete(competitor.id);
            }
          },
        },
      };
      series.push(competitorSeries);
      // @ts-expect-error: Incorrect type
      seriesMap[competitor.id] = competitorSeries;
    });

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.sortedWeeks.forEach((week, index) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      week.forEach(
        // @ts-expect-error: Incorrect type
        ({ supplementOrDiscount, days, hotelinfo, isAbsolute, hasNoData }) => {
          // First check if what the API returns is actually in the compset/own hotel
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          const currentSeries = seriesMap[hotelinfo.id];
          if (currentSeries) {
            if (hasNoData) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              const yValue = currentSeries.data.length
                ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  currentSeries.data[currentSeries.data.length - 1].y
                : null;

              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              currentSeries.data.push({
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                y: yValue,
                // marker to show a cross like in rates chart
                marker: HIGHCHARTS_MISSING_MARKER,
              });

              // Zone goes from the previous point to the next point (even when the x value is a date, addNoDataZone works with numeric values)
              addNoDataZone(
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                currentSeries.zones,
                { x: index - 1 },
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                { x: index + 1 },
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                index,
              );
            } else {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              const chartValue =
                this.entity === SUBTYPE_VALUES_KEY_STRATEGY
                  ? supplementOrDiscount
                  : days;
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              this.isAbsolute = isAbsolute;
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              currentSeries.data.push({ y: chartValue });
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              const lastZone =
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                currentSeries.zones[currentSeries.zones.length - 1];

              // If the last zone was a nodata zone, add a new zone
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              if (lastZone && lastZone.dashStyle === NO_DATA_ZONE.dashStyle) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                addZone(currentSeries.zones, index + 1);
              }
            }
          }
        },
      );
    });
    return series;
  }

  @computed(
    'args.containerHeight',
    'chartSeriesMinAndMAx.{max,min}',
    'sortedWeeks.[]',
    'value',
    'weeks',
    'x',
    'y',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    const chartOptions = {
      chart: {
        type: 'line',
      },
      xAxis: {
        type: 'category',
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        categories: this.weeks,
      },
      yAxis: {
        title: {
          text: '',
        },
        min: this.chartSeriesMinAndMAx.min,
        max: this.chartSeriesMinAndMAx.max,
        labels: {
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter() {
            if (that.entity === SUBTYPE_VALUES_KEY_STRATEGY) {
              const label = formatStrategyValue(
                that.intl,
                that.hotelAndMarketService,
                // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                this.value,
                that.isAbsolute,
              );

              return label === null ? label : `${label}`;
            }

            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            const label = formatCancellationpolicyValue(that.intl, this.value);

            return `${label}`;
          },
        },
      },
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        enabled: true,
        borderWidth: 0,
        itemStyle: {
          width: 200,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          color: 'var(--prism-color-text-neutral-subdued)',
        },
        itemHoverStyle: {
          color: 'var(--prism-color-interactive-text-secondary-hover)',
        },
      },
      tooltip: {
        hideDelay: 0.2,
        padding: 0,
        borderWidth: 0,
        shadow: false,
        shared: false,
        outside: true, // allows the tooltip to render outside the chart's SVG element box (required because else the chart tooltip can get cut off)
        shape: 'square',
        distance: 20,
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter() {
          begin();
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          that.week = this.x;

          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          const sortedWeek = that.sortedWeeks[that.weeks.indexOf(this.x)];
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
          that.hoveredHotels = sortedWeek
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            .filter(
              // @ts-expect-error: Incorrect type
              (week) =>
                // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                (week.supplementOrDiscount === this.y ||
                  // @ts-expect-error: Incorrect type
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  week.days === this.y) &&
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                !week.hasNoData,
            )
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
            .map((week) => week.hotelinfo.id);

          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
          that.tooltipData = sortedWeek
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            .sort((a, b) => a.formattedValue - b.formattedValue)
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            .reverse();

          end();
          // @ts-expect-error: Incorrect type
          return document.getElementById(
            'dummy-tooltip-for-rates-strategy-evolution',
          ).innerHTML;
        },
        useHTML: true,
      },
    };

    if (this.args.containerHeight) {
      // @ts-expect-error: Incorrect type
      chartOptions.chart.height = this.args.containerHeight;
    }

    return chartOptions;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartSeriesMinAndMAx() {
    // There is no default min and max, therefore it's defined with the values in the chart itself so it's as centric as possible
    // @ts-expect-error: Incorrect type
    const allValues = [];
    const isStrategy = this.entity === SUBTYPE_VALUES_KEY_STRATEGY;
    const margin = isStrategy && this.type !== TYPE_ID_MEALTYPE ? 0.02 : 3;
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.sortedWeeks.forEach((week) => {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      week.forEach(({ supplementOrDiscount, days, hasNoData }) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const chartValue = isStrategy ? supplementOrDiscount : days;

        if (!hasNoData) {
          allValues.push(chartValue);
        }
      });
    });
    return {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      min: Math.min(...allValues) - margin,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      max: Math.max(...allValues) + margin,
    };
  }

  @computed('args.params.entity')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get entity() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.params?.entity;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isEmpty() {
    // Don't show NO DATA when still loading
    if (this.isLoading) {
      return false;
    }

    // For all week and per hotel, check if all of them are empty, only show NO DATA when all of them are...
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.sortedWeeks.every((week) => {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      return week.every((strategy) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        return strategy.hasNoData;
      });
    });
  }

  @computed('args.params.isLoading')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLoading() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.params?.isLoading;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ownHotelInfo() {
    return this.hotelAndMarketService.hotel?.hotelinfo;
  }

  @computed('args.params.formattedHistoricData')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sortedWeeks() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.params?.formattedHistoricData;
  }

  @computed('args.params.type')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get type() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.params?.type;
  }

  @computed('args.params.formattedDates')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get weeks() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.params?.formattedDates;
  }

  <template>
    <div class="position-relative">
      <GeneralChartsOtaChart
        {{! @glint-expect-error: Incorrect type }}
        @chartOptions={{this.chartOptions}}
        @model={{this.chartData}}
      />
      {{#if this.isEmpty}}
        <div class="message-overlay">
          <div class="message-wrapper">
            {{t "general.no_data"}}
          </div>
        </div>
      {{/if}}
    </div>

    <div class="hidden" id="dummy-tooltip-for-rates-strategy-evolution">
      <div
        class="default-tooltip default-tooltip--no-max-width default-table-tooltip"
      >
        <HotelStrategyTooltipsEvolutionChartTooltip
          @data={{this.tooltipData}}
          @entity={{this.entity}}
          @hoveredHotels={{this.hoveredHotels}}
          @type={{this.type}}
          @week={{this.week}}
        />
      </div>
    </div>
  </template>
}
